import React, { useEffect, useState, createContext } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { DASHBOARD, LOGIN, STORE } from 'constants/routes';
import * as Roles from 'constants/roles';
import { useAuthStore } from 'stores/authStore';

export const PermissionContext = createContext();

const SEGMENT_TITLES = {
  'brand-manager': 'Brand Manager',
  banners: 'Banners',
  videos: 'Videos',
  onboarding: 'Onboarding',
  dashboard: 'Dashboard',
  'brand-information': 'Brand Information',
  theme: 'Theme Customization',
  products: 'Products',
  'products-groups': 'Product Groups',
  'product-catalog': 'Product Catalog',
  parent_products: 'Parent Products',
  child_products: 'Child Products',
  colors: 'Colors',
  sizes: 'Sizes',
  categories: 'Categories',
  inventories: 'Inventories',
  retailers: 'Retailers',
  retailer: 'Retailer',
  'retailer-looks': 'Retailer Looks',
  'retailer-locations': 'Retailer Locations',
  'retailer-location-groups': 'Retailer Location Groups',
  store: 'Store',
  regions: 'Regions',
  'region-looks': 'Region Looks',
  'region-look': 'Region Look',
  tiers: 'Tiers',
  brands: 'Brands',
  brand_content: 'Brand Content',
  'featured-artist': 'Featured Artist',
  admins: 'Admins',
  stylists: 'Stylists',
  'landing-page': 'Landing Page',
  profile: 'Profile',
  look: 'Look',
  'owner-group-page': 'Owner Group Page',
  widgets: 'Widgets',
  'favorite-products': 'Favorite Products',
  'default-look-image': 'Default Look Image',
  'how-to-hub': 'How To Hub',
  'how-to-hub-retailer': 'How To Hub',
  'checkout-config': 'Checkout Config',
  'cover-photo': 'Cover Photo',
  events: 'Events',
  locations: 'Locations',
  'location-groups': 'Location Groups',
  orders: 'Orders',
  'terms-of-service': 'Terms of Service',
  'privacy-policy': 'Privacy Policy',
  'add-number': 'Add Number',
  'page-builder': 'Page Builder',
};

function getPageTitle(pathname) {
  const segment = pathname.split('/').filter(Boolean)[0];
  return SEGMENT_TITLES[segment] ?? 'Brand Manager';
}

export function ProtectedRoute({ children, condition = true }) {
  const [userPermissions, setUserPermissions] = useState();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const role = useAuthStore((state) => state.role);
  const isRetailer = role === Roles.RETAILER;
  const pageName = location.pathname.split('/').filter(Boolean)[0] || 'events';
  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => {
    if (user) {
      if (isRetailer && pageName !== 'events' && pageName !== 'widgets') {
        setUserPermissions({
          read: true,
          create: false,
          update: false,
          delete: false,
        });
      } else {
        setUserPermissions({
          read: true,
          create: true,
          update: true,
          delete: true,
        });
      }
    }
  }, [user, isRetailer, pageName]);

  if (!user?.email) {
    return <Navigate to={LOGIN} state={{ from: location }} replace />;
  }

  if (!condition) {
    return <Navigate to={isRetailer ? STORE : DASHBOARD} replace />;
  }

  return (
    <PermissionContext.Provider value={{ permission: userPermissions }}>
      {children}
    </PermissionContext.Provider>
  );
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  condition: PropTypes.bool,
};
